import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { FocusShell } from "@/components/layout/FocusShell";
import { Button, ButtonLink, Notice, Panel } from "@/components/system";
import { useCity } from "@/contexts/CityContext";
import { track } from "@/lib/analytics";
import { confirmBriefSubscription, peekBriefConfirmation } from "@/lib/brief";
import { SEO } from "@/lib/seo";

export default function BriefConfirm() {
  const { token = "" } = useParams<{ token: string }>();
  const { city } = useCity();
  const [state, setState] = useState<"idle" | "submitting" | "success" | "error">(
    "idle",
  );
  const peek = useQuery({
    queryKey: ["brief-confirm", city.slug, token],
    queryFn: () => peekBriefConfirmation(city.slug, token),
    enabled: Boolean(token),
    retry: false,
  });

  const confirm = async () => {
    setState("submitting");
    try {
      await confirmBriefSubscription(city.slug, token);
      track("brief_confirm");
      setState("success");
    } catch {
      setState("error");
    }
  };

  return (
    <FocusShell>
      <SEO
        title="Confirm the Canberra Referral Brief · PulseBoard"
        description="Confirm your subscription to the Canberra Referral Brief."
        path={`/brief/confirm/${token}`}
        noindex
      />
      <Panel>
        <p className="ds-eyebrow ds-eyebrow--teal">{city.name} Referral Brief</p>
        {peek.isLoading ? <p className="ds-small" role="status">Checking your confirmation link…</p> : null}
        {peek.isError ? (
          <div className="ds-stack" style={{ gap: 8 }} role="alert">
            <h1 className="ds-display-2">This confirmation link has expired</h1>
            <p className="ds-small">It may have been used already. Subscribe again from the brief page to get a new link.</p>
          </div>
        ) : null}
        {peek.data && state !== "success" ? (
          <div className="ds-stack" style={{ gap: 14 }}>
            <h1 className="ds-display-2">Confirm your weekly brief</h1>
            <p className="ds-body">
              Confirm <strong>{peek.data.email_masked}</strong> to receive the Tuesday morning referral brief.
            </p>
            {peek.data.status === "confirmed" ? (
              <Notice tone="success">This subscription is already confirmed.</Notice>
            ) : (
              <div>
                <Button disabled={state === "submitting"} onClick={() => void confirm()}>
                  {state === "submitting" ? "Confirming…" : "Confirm subscription"}
                </Button>
              </div>
            )}
            {state === "error" ? <Notice tone="error">Confirmation didn't go through. Open the link from your email again.</Notice> : null}
          </div>
        ) : null}
        {state === "success" ? (
          <div className="ds-stack" style={{ gap: 8 }} role="status">
            <h1 className="ds-display-2">You're confirmed</h1>
            <p className="ds-body">The {city.name} Referral Brief arrives on Tuesday mornings.</p>
          </div>
        ) : null}
        <div><ButtonLink to="/brief" variant="secondary">Go to the brief</ButtonLink></div>
      </Panel>
    </FocusShell>
  );
}
